// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {HttpStatus} from '@fluxer/constants/src/HttpConstants';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const logger = new Logger('VoiceSeatCommands');

const VIDEO_CHANGES_BUSY_DESCRIPTOR = msg({
	message: 'Too many video changes right now. Try again in a moment.',
	comment:
		'3D voice room TV: short notice when playing, pausing or skipping the shared video was refused because it changed too often (rate limit). Retrying a little later works.',
});
const VIDEO_UNREACHABLE_DESCRIPTOR = msg({
	message: 'The room TV cannot reach the voice server right now. Try again in a moment.',
	comment:
		'3D voice room TV: short notice when playing, pausing or skipping the shared video failed because the server could not reach the voice server (503). Retrying a little later usually works.',
});
// The API takes a position up to a day into the video.
const MAX_VIDEO_POSITION_MS = 86_400_000;

const SEAT_CHANGES_BUSY_DESCRIPTOR = msg({
	message: 'Lots of people are changing seats right now. Try again in a moment.',
	comment:
		'3D voice room: short notice when a seat could not be taken because too many seat changes happen in this channel at once (rate limit). Retrying a second later works.',
});

// Asks the server for a lounge or watch seat (null = any free lounge seat). The client never writes the seat itself
// : the server answers through the participant's `micup.seat` LiveKit attribute, which the room reads. The
// channel allows a limited number of seat changes a second (429); that one is told for a seat the user picked,
// never for the room's own any-seat request (useSeatFallback), which nobody asked for. The rest are only logged.
export async function takeVoiceSeat(channelId: string, seat: string | null): Promise<void> {
	const connectionId = MediaEngine.channelId === channelId ? MediaEngine.connectionId : null;
	if (!connectionId) return;
	try {
		await http.put(Endpoints.CHANNEL_VOICE_SEAT(channelId), {body: {connection_id: connectionId, seat}});
	} catch (error) {
		logger.warn('Voice seat request failed', error);
		if (seat !== null && error instanceof HttpError && error.status === HttpStatus.TOO_MANY_REQUESTS)
			ToastCommands.createToast({type: 'info', children: i18n._(SEAT_CHANGES_BUSY_DESCRIPTOR)});
	}
}

// Asks the server to write my worn room character on my own connection again (`micup.character`); the value comes
// from the server's user record, never from the client. 403 means characters are off there: nothing to tell or log.
export async function refreshRoomCharacter(channelId: string): Promise<void> {
	const connectionId = MediaEngine.channelId === channelId ? MediaEngine.connectionId : null;
	if (!connectionId) return;
	try {
		await http.post(Endpoints.CHANNEL_VOICE_SEAT_CHARACTER(channelId), {body: {connection_id: connectionId}});
	} catch (error) {
		if (error instanceof HttpError && error.status === HttpStatus.FORBIDDEN) return;
		logger.warn('Room character refresh failed', error);
	}
}

// Plays a YouTube video on the room TV for everyone, from positionMs, paused or not; the same call pauses, plays and
// skips. The server checks the link again and writes the `micup.watch` attribute of my connection; only the id leaves
// here, as its plain watch link. Resolves to that connection id, or null when it was not taken (429 and 503 are told,
// the rest only logged).
export async function setRoomVideo(
	channelId: string,
	{videoId, positionMs, paused}: {videoId: string; positionMs: number; paused: boolean},
): Promise<string | null> {
	const connectionId = MediaEngine.channelId === channelId ? MediaEngine.connectionId : null;
	if (!connectionId) return null;
	try {
		await http.put(Endpoints.CHANNEL_VOICE_SEAT_WATCH(channelId), {
			body: {
				connection_id: connectionId,
				url: `https://www.youtube.com/watch?v=${videoId}`,
				position_ms: Math.min(MAX_VIDEO_POSITION_MS, Math.max(0, Math.round(positionMs))),
				paused,
			},
		});
		return connectionId;
	} catch (error) {
		logger.warn('Room video request failed', error);
		if (error instanceof HttpError && error.status === HttpStatus.TOO_MANY_REQUESTS)
			ToastCommands.createToast({type: 'info', children: i18n._(VIDEO_CHANGES_BUSY_DESCRIPTOR)});
		if (error instanceof HttpError && error.status === HttpStatus.SERVICE_UNAVAILABLE)
			ToastCommands.createToast({type: 'info', children: i18n._(VIDEO_UNREACHABLE_DESCRIPTOR)});
		return null;
	}
}

// Stops the video of my connection on the room TV for everyone.
export async function stopRoomVideo(channelId: string): Promise<void> {
	const connectionId = MediaEngine.channelId === channelId ? MediaEngine.connectionId : null;
	if (!connectionId) return;
	try {
		await http.delete(Endpoints.CHANNEL_VOICE_SEAT_WATCH(channelId), {body: {connection_id: connectionId}});
	} catch (error) {
		logger.warn('Room video stop failed', error);
	}
}

// A moderator (MUTE_MEMBERS or MOVE_MEMBERS) stops another member's video on the room TV for everyone.
export async function stopMemberRoomVideo(channelId: string, userId: string): Promise<void> {
	try {
		await http.delete(Endpoints.CHANNEL_VOICE_SEAT_WATCH_MEMBER(channelId, userId));
	} catch (error) {
		logger.warn('Room video moderator stop failed', error);
	}
}
